import { ValidateCheckbox } from "@/Checkbox";
import DateButton from "@/common/DateButton";
import { ValidateServiceCard } from "@/common/ServiceCard";
import { Divider } from "@/Divider";
import MessageModal from "@/MessageModal";
import { ValidateSelectBoxImg } from "@/SelectBoxImg";
import { useEffect, useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import useOpenModal from "^/common/useOpenModal";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { Button } from "@/Button";
import { formatDate, formatDateDot, formatDateToYYYYMMDD } from "%/date";
import { ValidateSelectList } from "@/SelectList";
import {
  useReserveForms,
  useCalculateCoin,
  useCreateReserve,
  // useHasBalanceCoin, // 현재 사용하지 않음
  useUpdateTempReserve,
  useReservableTime,
  checkReservationCapacity,
} from "~/services/reservation";
import { useReservationDisabledDates } from "^/reservation/useReservationDisabledDates";
import { useTicketList } from "~/services/customer/myPage";
import { getErrorMessage } from "%/errorCode";
// import { useTossPreOrder } from "~/services/payment/toss";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { PetDataType } from "~/types/common";
import { Dialog } from "@/Dialog";
import ContractModal from "@/common/ContractModal";
import ReserveConfirmDialog from "./ReserveConfirmDialog";
import { useRouter } from "@tanstack/react-router";

interface SelectList {
  value: string;
  label: string;
}
interface ReservationClassState {
  storeName: string;
  storeType: string;
  services: string;
  animal: string;
  agree: boolean;
  startDate: Date | null;
  endDate: Date | null;
  startHour: SelectList | null;
  // 데이케어 시간권: 이용시간(시간 단위). endTime = startHour + useHours 로 계산됨
  useHours: SelectList | null;
  [key: string]: any; // 인덱스 시그니처 추가
}

const ReservationDaycare = ({
  storeName,
  storeType,
  storeData,
  petList,
  date,
}: {
  storeName: string;
  storeType: string;
  storeData: {
    value: string;
    label: string;
  };
  petList: PetDataType[];
  date: any;
}) => {
  const { history } = useRouter();
  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    setValue,
    watch,
  } = useForm<ReservationClassState>({
    defaultValues: {
      storeName: storeName,
      storeType: storeType,
      services: "",
      animal: "",
      startDate: null,
      endDate: null,
      startHour: null,
      useHours: null,
      agree: false,
    },
    mode: "onChange",
  });
  // const [ setDate] = useState(getToday());
  const { isOpenModal, clickModal, closeModal } = useOpenModal();
  const [isStart, setIsStart] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractMessage, setContractMessage] = useState("");
  const [showCoinShortageDialog, setShowCoinShortageDialog] = useState(false);
  const [showReservationCompleteDialog, setShowReservationCompleteDialog] =
    useState(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [reservationSeq, setReservationSeq] = useState<number | null>(null);
  const [paymentOrderData] = useState<any>(null); // 결제 프로세스에서 사용 예정
  const [selectedPetSeq, setSelectedPetSeq] = useState<string | null>(
    petList && petList.length > 0 ? petList[0].petSeq.toString() : null
  );
  const [showGradeConfirmDialog, setShowGradeConfirmDialog] = useState(false);
  // 유치원 정원 초과 안내 팝업 (초과 날짜)
  const [capacityFullDate, setCapacityFullDate] = useState<string | null>(null);
  // 예약 신청 전 최종 확인 대기중인 폼 데이터
  const [pendingReserveData, setPendingReserveData] =
    useState<ReservationClassState | null>(null);
  const [duplicateReserveInfo, setDuplicateReserveInfo] = useState<{
    mode: "same" | "other";
    conflictCompanyName: string;
  } | null>(null);
  const lastReserveRequestRef = useRef<any>(null);

  // 토스페이먼츠 관련 refs
  const tossPaymentsRef = useRef<any>(null);
  const widgetsRef = useRef<any>(null);
  const paymentMethodWidgetRef = useRef<any>(null);
  const agreementWidgetRef = useRef<any>(null);
  // const [gradeConfirm, setGradeConfirm] = useState(false); // 테스트를 위해 false로 설정

  // petList가 로드되면 첫 번째 반려동물을 자동으로 선택하고 폼 값도 설정
  useEffect(() => {
    if (petList && petList.length > 0) {
      const firstPetSeq = petList[0].petSeq.toString();
      setSelectedPetSeq(firstPetSeq);
      setValue("animal", firstPetSeq, { shouldValidate: true });
    }
  }, [petList, setValue]);

  useEffect(() => {
    if (date) {
      setValue("startDate", new Date(date), { shouldValidate: true });
      setStartDate(new Date(date));
    }
  }, [date]);

  // 토스페이먼츠 SDK 초기화 및 위젯 렌더링 (통합)
  useEffect(() => {
    if (!showPaymentScreen || !paymentOrderData) return;

    let isMounted = true;
    console.log("🚀 결제 팝업 열림 - 통합 초기화 시작");

    const initializeAndRenderWidgets = async () => {
      try {
        // 1단계: 토스페이먼츠 SDK 초기화
        console.log("1️⃣ 토스페이먼츠 SDK 초기화 시작");
        const clientKey = paymentOrderData.widgetClientKey;
        const redirectUrl =
          import.meta.env.VITE_TOSS_REDIRECT_URL ||
          "https://app.mungtoday.com/api/payment/toss/brandpay/auth";

        const toss = await loadTossPayments(clientKey, redirectUrl);
        if (!isMounted) return;

        tossPaymentsRef.current = toss;
        console.log("✅ 1단계: 토스페이먼츠 SDK 초기화 완료");

        // 2단계: DOM 요소가 준비될 때까지 대기
        console.log("2️⃣ DOM 요소 대기 시작");
        await new Promise((resolve) => setTimeout(resolve, 300)); // 팝업 렌더링 대기

        const waitForElement = (id: string, maxAttempts = 30) => {
          return new Promise<HTMLElement>((resolve, reject) => {
            let attempts = 0;
            const checkElement = () => {
              const element = document.getElementById(id);
              if (element) {
                console.log(`✅ DOM 요소 ${id} 찾음`);
                resolve(element);
              } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkElement, 100);
              } else {
                reject(
                  new Error(
                    `❌ Element with id "${id}" not found after ${maxAttempts} attempts`
                  )
                );
              }
            };
            checkElement();
          });
        };

        await waitForElement("payment-methods");
        await waitForElement("agreement");
        console.log("✅ 2단계: DOM 요소 준비 완료");

        // 3단계: 기존 위젯 정리
        console.log("3️⃣ 기존 위젯 정리");
        if (paymentMethodWidgetRef.current) {
          await paymentMethodWidgetRef.current.destroy();
          paymentMethodWidgetRef.current = null;
        }
        if (agreementWidgetRef.current) {
          await agreementWidgetRef.current.destroy();
          agreementWidgetRef.current = null;
        }

        // 4단계: 위젯 인스턴스 생성
        console.log("4️⃣ 위젯 인스턴스 생성");
        widgetsRef.current = tossPaymentsRef.current.widgets({
          customerKey: paymentOrderData.customerKey,
          brandpay: {
            redirectUrl: redirectUrl,
          },
        });

        // 5단계: 결제 금액 설정
        console.log("5️⃣ 결제 금액 설정", paymentOrderData.payAmount);
        await widgetsRef.current.setAmount({
          value: paymentOrderData.payAmount,
          currency: "KRW",
        });

        // 6단계: 결제 수단 위젯 렌더링
        console.log("6️⃣ 결제 수단 위젯 렌더링");
        const paymentMethodWidget =
          await widgetsRef.current.renderPaymentMethods({
            selector: "#payment-methods",
            variantKey: "DEFAULT",
          });

        // 7단계: 약관 위젯 렌더링
        console.log("7️⃣ 약관 위젯 렌더링");
        const agreementWidget = await widgetsRef.current.renderAgreement({
          selector: "#agreement",
        });

        // 8단계: 위젯 인스턴스 저장 및 이벤트 설정
        console.log("8️⃣ 위젯 인스턴스 저장 및 이벤트 설정");
        paymentMethodWidgetRef.current = paymentMethodWidget;
        agreementWidgetRef.current = agreementWidget;

        paymentMethodWidget.on(
          "paymentMethodSelect",
          (selectedPaymentMethod: any) => {
            console.log("선택된 결제수단:", selectedPaymentMethod);
          }
        );

        console.log("🎉 모든 단계 완료 - 결제 위젯 렌더링 성공");
      } catch (error) {
        console.error("💥 결제 위젯 초기화/렌더링 실패:", error);
        alert(
          "결제 위젯을 불러오는데 실패했습니다. 새로고침 버튼을 클릭해주세요."
        );
      }
    };

    // 팝업이 열린 후 실행
    const timeoutId = setTimeout(() => {
      initializeAndRenderWidgets();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [showPaymentScreen, paymentOrderData]);

  const cdProductType = "PRODUCT_TYPE_DAYCARE";

  const { data: reserveForms } = useReserveForms(
    parseInt(storeData.value),
    selectedPetSeq ? Number(selectedPetSeq) : 0,
    cdProductType,
    !!selectedPetSeq
  );
  console.log(reserveForms);

  // 데이케어 시간권 이용시간(Select) 범위
  // minUseHours: 최소 이용 시간(없으면 1), maxUseHours: 보유 이용권 잔여 기준 최대 이용 시간
  const minUseHours = reserveForms?.reserveForm?.minUseHours ?? 1;
  const maxUseHours = reserveForms?.reserveForm?.maxUseHours ?? null;
  // 보유 이용권이 없으면(maxUseHours == null) 예약 불가
  const hasDaycareTicket = maxUseHours != null && maxUseHours >= minUseHours;

  // 이용시간 Select 옵션 (minUseHours ~ maxUseHours, 시간 단위)
  const useHoursOptions: SelectList[] = hasDaycareTicket
    ? Array.from({ length: maxUseHours - minUseHours + 1 }, (_, i) => {
        const h = minUseHours + i;
        return { value: h.toString(), label: `${h}시간` };
      })
    : [];

  // 보유 데이케어 이용권 목록 (해당 매장 + 유효) → 만료 임박 순(FIFO)으로 차감 대상 자동 선택
  const { data: heldTicketList = [] } = useTicketList(
    true,
    parseInt(storeData.value)
  );
  const selectedDaycareTicket =
    [...heldTicketList]
      .filter(
        (t) =>
          t.cdProductType === cdProductType && (t.remainCount ?? 0) > 0
      )
      .sort((a, b) => {
        const da = a.expiredDate
          ? new Date(a.expiredDate).getTime()
          : Infinity;
        const db = b.expiredDate
          ? new Date(b.expiredDate).getTime()
          : Infinity;
        return da - db;
      })[0] ?? null;

  // 코인 계산 API 훅
  const { mutate: calculateCoin, data: calculateCoinData } = useCalculateCoin();

  // 예약 생성 API 훅
  const { mutate: createReserve, isPending: isCreatingReserve } =
    useCreateReserve();

  // 임시 예약 업데이트 API 훅
  const { mutate: updateTempReserve } = useUpdateTempReserve();

  // 토스페이먼츠 API 훅
  // const { mutate: createTossPreOrder } = useTossPreOrder();

  // 코인 잔액 확인 API 훅 (현재 사용하지 않음)
  // const totalCoin = calculateCoinData?.data?.paymentCoinTotal || 0;
  // const { data: hasBalanceCoinData, isLoading: isCheckingBalance } =
  //   useHasBalanceCoin(totalCoin);

  // 예약 가능 시간 조회 API 훅
  const { data: reservableTimeData } = useReservableTime(
    parseInt(storeData.value),
    startDate ? formatDateToYYYYMMDD(startDate) : "",
    !!startDate
  );

  // 예약 불가 날짜(정기휴무 + 임시휴무) → 캘린더 비활성화
  const { disabledDates, handleMonthNavigate } = useReservationDisabledDates(
    parseInt(storeData.value),
    "PRODUCT_TYPE_DAYCARE"
  );

  // 시간 옵션 생성 함수
  const generateTimeOptions = (startTime: string, endTime: string) => {
    const options: Array<{ value: string; label: string }> = [];

    // 시작 시간과 종료 시간을 숫자로 변환 (HH:MM:SS -> HH)
    const startHour = parseInt(startTime.split(":")[0]);
    const endHour = parseInt(endTime.split(":")[0]);

    // 시작 시간부터 종료 시간까지 한 시간 간격으로 옵션 생성
    for (let hour = startHour; hour <= endHour; hour++) {
      options.push({
        value: hour.toString().padStart(2, "0"),
        label: hour.toString().padStart(2, "0"),
      });
    }

    return options;
  };

  // 시작 시간과 종료 시간 옵션
  const startHourOptions =
    reservableTimeData?.startTime && reservableTimeData?.endTime
      ? generateTimeOptions(
          reservableTimeData.startTime,
          reservableTimeData.endTime
        )
      : [];

  // 시작시간(정시) + 이용시간(시간 수)으로 종료시간(HH:00:00) 계산
  const computeEndTime = (
    startHourValue?: string,
    useHoursValue?: string
  ): string | null => {
    if (!startHourValue || !useHoursValue) return null;
    const end = parseInt(startHourValue, 10) + parseInt(useHoursValue, 10);
    return `${end.toString().padStart(2, "0")}:00:00`;
  };

  // 폼 값들을 watch로 감지
  const watchedValues = watch();

  // 디버깅: 폼 상태 확인
  console.log("=== 폼 상태 디버깅 ===");
  console.log("isValid:", isValid);
  console.log("errors:", errors);
  console.log("watchedValues:", watchedValues);
  console.log("startDate:", startDate);
  console.log("endDate:", endDate);
  console.log("selectedPetSeq:", selectedPetSeq);
  console.log("reservableTimeData:", reservableTimeData);
  console.log("startHourOptions:", startHourOptions);
  console.log("useHoursOptions:", useHoursOptions);
  console.log("=== 디버깅 끝 ===");

  // 코인 계산 API 호출 함수
  const callCalculateCoin = () => {
    if (!reserveForms || !selectedPetSeq) return;

    // 선택된 반려동물 찾기
    const selectedPet = petList?.find(
      (pet: PetDataType) => pet.petSeq.toString() === selectedPetSeq
    );
    if (!selectedPet) return;
    console.log(watchedValues.startHour);
    // 요청 데이터 구성
    const requestData = {
      reservationSeq: null,
      cdProductType: cdProductType,
      gradeConfirm: reserveForms.gradeConfirm,
      paymentCoinTotal: null,
      companySeq: parseInt(storeData.value),
      reserveForm: {
        productSeq: reserveForms.reserveForm.productSeq, // 기본값 설정
        dtReservStart: watchedValues.startDate
          ? formatDate(watchedValues.startDate)
          : null,
        startTime: watchedValues.startHour?.value
          ? `${watchedValues.startHour.value}:00:00`
          : null,
        endTime: computeEndTime(
          watchedValues.startHour?.value,
          watchedValues.useHours?.value
        ),
        // dayOfWeeks: [
        //   { cdDayOfWeek: "MONDAY", dayOfWeekName: "월요일", select: false },
        //   { cdDayOfWeek: "TUESDAY", dayOfWeekName: "화요일", select: false },
        //   { cdDayOfWeek: "WEDNESDAY", dayOfWeekName: "수요일", select: false },
        //   { cdDayOfWeek: "THURSDAY", dayOfWeekName: "목요일", select: false },
        //   { cdDayOfWeek: "FRIDAY", dayOfWeekName: "금요일", select: false },
        //   { cdDayOfWeek: "SATURDAY", dayOfWeekName: "토요일", select: false },
        //   { cdDayOfWeek: "SUNDAY", dayOfWeekName: "일요일", select: false },
        // ],
        paymentCoin: reserveForms.reserveForm.paymentCoin, // 기본값 설정
        additionals: reserveForms.reserveForm.additionals.map(
          (additional: {
            additionalCompanySeq?: number;
            additionalName: string;
            paymentCoin: number;
          }) => ({
            ...additional,
            active: true, // 기본값 설정
            select:
              watchedValues.services?.includes(
                additional.additionalCompanySeq?.toString() || ""
              ) || false,
          })
        ),
        pets:
          petList?.map((pet: PetDataType) => ({
            petSeq: pet.petSeq,
            petName: pet.petName,
            select: pet.petSeq.toString() === selectedPetSeq,
          })) || [],
        memo: null,
      },
    };

    console.log("코인 계산 API 호출:", requestData);
    calculateCoin(requestData);
  };

  // 조건에 따라 코인 계산 API 호출
  useEffect(() => {
    // 1. 날짜를 선택했을 때
    // 2. 시간을 선택했을 때 (시, 분)
    // 3. 부가서비스를 선택했을 때
    // 4. 반려동물을 선택했을 때

    const hasDate = watchedValues.startDate;
    const hasTime = watchedValues.startHour && watchedValues.useHours;
    const hasPetSelected = selectedPetSeq;

    if (hasDate && hasTime && hasPetSelected && reserveForms) {
      callCalculateCoin();
    }
  }, [
    watchedValues.startDate,
    watchedValues.startHour,
    watchedValues.useHours,
    watchedValues.services,
    selectedPetSeq,
    reserveForms,
  ]);

  const handleDateClick = (isNext: boolean) => {
    if (isNext) {
      setIsStart(true);
    } else {
      setIsStart(false);
    }
    clickModal();
    // 달력 모달 열기 로직
    // alert("여기에 달력 모달을 띄우세요!");
  };

  const handleDaySelect = (day: Date | null) => {
    if (!day) return;

    // const formattedDate = formatDate(day);

    // 데이케어는 시작일과 종료일이 같음
    setValue("startDate", day, { shouldValidate: true });
    setValue("endDate", day, { shouldValidate: true });
    setStartDate(day);
    setEndDate(day);
  };

  const bindSubmit = async (data: ReservationClassState) => {
    console.log("예약 데이터:", data);

    if (!reserveForms || !selectedPetSeq) {
      console.error("필수 데이터가 없습니다.");
      return;
    }

    // gradeConfirm 체크
    if (!reserveForms.gradeConfirm) {
      console.error("해당 반려견의 체급은 수용이 불가능합니다.");
      setShowGradeConfirmDialog(true);
      return;
    }

    // 코인 잔액 확인
    // if (isCheckingBalance) {
    //   console.log("코인 잔액 확인 중...");
    //   return;
    // }

    // if (hasBalanceCoinData) {
    //   console.log("코인 잔액 확인 결과:", hasBalanceCoinData);

    //   // 코인 잔액이 부족한 경우 처리
    //   if (!hasBalanceCoinData.data.coinPurchasable) {
    //     console.error("코인 잔액이 부족합니다.");
    //     setShowCoinShortageDialog(true);
    //     return;
    //   }
    // }
    // // 선택된 반려동물 찾기
    // const selectedPet = petList?.find(
    //   (pet: PetDataType) => pet.petSeq.toString() === selectedPetSeq
    // );
    // console.log(selectedPet);
    // if (!selectedPet) {
    //   console.error("선택된 반려동물을 찾을 수 없습니다.");
    //   return;
    // }

    // 보유 데이케어 이용권이 없으면 예약 불가
    if (!hasDaycareTicket || !selectedDaycareTicket) {
      alert("보유한 데이케어 이용권이 없습니다.");
      return;
    }

    // 유치원 정원 체크 — 선택한 날짜가 정원 초과면 안내 팝업 후 중단.
    // 데이케어는 단일 날짜. 체크 실패 시엔 안전하게 예약을 그대로 진행한다.
    const reserveDateStr = data.startDate
      ? formatDate(new Date(data.startDate))
      : null;
    if (reserveDateStr) {
      try {
        const cap = await checkReservationCapacity(parseInt(storeData.value), [
          reserveDateStr,
        ]);
        if (cap.limitEnabled && cap.fullDates.includes(reserveDateStr)) {
          setCapacityFullDate(reserveDateStr);
          return;
        }
      } catch (e) {
        console.warn("정원 체크 실패 — 예약 진행", e);
      }
    }

    // 선택한 이용시간(시간 수)
    const useHoursNum = data.useHours?.value
      ? parseInt(data.useHours.value, 10)
      : 0;

    // 예약 생성 요청 데이터 구성
    const requestData = {
      reservationSeq: null,
      cdProductType: cdProductType,
      gradeConfirm: reserveForms.gradeConfirm,
      paymentCoinTotal: calculateCoinData?.data?.paymentCoinTotal || null,
      companySeq: parseInt(storeData.value),
      // 데이케어 시간권: 보유 이용권을 이용시간(시간 수)만큼 차감
      tickets: [
        {
          purchaseTicketSeq: selectedDaycareTicket.purchaseTicketSeq,
          productTicketSeq: selectedDaycareTicket.productTicketSeq,
          paymentTicket: useHoursNum,
        },
      ],
      reserveForm: {
        productSeq: reserveForms.reserveForm.productSeq,
        dtReservStart: data.startDate
          ? formatDate(new Date(data.startDate))
          : null,
        startTime: data.startHour?.value
          ? `${data.startHour.value}:00:00`
          : null,
        endTime: computeEndTime(data.startHour?.value, data.useHours?.value),
        // dtReservEnd: data.endDate ? formatDate(new Date(data.endDate)) : null,
        dayOfWeeks: [], // 데이케어는 요일 선택이 없음
        paymentCoin: reserveForms.reserveForm.paymentCoin,
        additionals: reserveForms.reserveForm.additionals.map(
          (additional: {
            additionalCompanySeq?: number;
            additionalName: string;
            paymentCoin: number;
          }) => ({
            ...additional,
            active: true,
            select:
              data.services?.includes(
                additional.additionalCompanySeq?.toString() || ""
              ) || false,
          })
        ),
        pets:
          petList?.map((pet: PetDataType) => ({
            petSeq: pet.petSeq,
            petName: pet.petName,
            select: pet.petSeq.toString() === selectedPetSeq,
          })) || [],
        memo: null,
      },
    };

    console.log("예약 생성 API 호출:", requestData);
    submitReserveRequest({ ...requestData, forceProceed: false });
  };

  const submitReserveRequest = (requestData: any) => {
    lastReserveRequestRef.current = requestData;
    createReserve(requestData as any, {
      onSuccess: (response) => {
        console.log("예약 생성 응답:", response);
        if (response.statusCode === 4009) {
          setDuplicateReserveInfo({
            mode: "same",
            conflictCompanyName: response.data?.conflictCompanyName ?? "",
          });
          return;
        }
        if (response.statusCode === 4010) {
          setDuplicateReserveInfo({
            mode: "other",
            conflictCompanyName: response.data?.conflictCompanyName ?? "",
          });
          return;
        }
        if (response.statusCode === 200) {
          if (response.data?.responseCode === "DAYCARE_RESERVE_REQUESTED") {
            setReservationSeq(response.data?.reservationSeq || null);
            handlePaymentProcess();
          } else if (response.data?.responseCode === "INVALID_CONTRACT") {
            setReservationSeq(response.data?.reservationSeq || null);
            setContractMessage(response.message || "계약이 필요합니다.");
            setShowContractDialog(true);
          } else {
            alert("예약이 성공적으로 완료되었습니다.");
          }
        }
      },
      onError: (error: unknown) => {
        console.error("예약 생성 실패:", error);
        alert(getErrorMessage(error, "예약 생성에 실패했습니다. 다시 시도해주세요."));
      },
    });
  };

  const handleForceProceedReserve = () => {
    const last = lastReserveRequestRef.current;
    setDuplicateReserveInfo(null);
    if (!last) return;
    submitReserveRequest({ ...last, forceProceed: true });
  };

  // 결제 프로세스 시작
  const handlePaymentProcess = () => {
    if (!calculateCoinData?.data?.paymentCoinTotal) {
      console.error("결제 금액이 없습니다.");
      return;
    }

    // const paymentAmount = calculateCoinData.data.paymentCoinTotal;

    // createTossPreOrder(
    //   { amount: paymentAmount, isRecurringYn: "N" },
    //   {
    //     onSuccess: (response) => {
    //       console.log("토스페이먼츠 Pre Order 성공:", response);
    //       setPaymentOrderData(response.data);
    //       setShowPaymentScreen(true);
    //     },
    //     onError: (error) => {
    //       console.error("토스페이먼츠 Pre Order 실패:", error);
    //       alert("결제 준비에 실패했습니다. 다시 시도해주세요.");
    //     },
    //   }
    // );
  };

  // 결제 팝업 닫기 함수
  const handleClosePaymentPopup = async () => {
    try {
      // 위젯 정리
      if (paymentMethodWidgetRef.current) {
        await paymentMethodWidgetRef.current.destroy();
        paymentMethodWidgetRef.current = null;
      }
      if (agreementWidgetRef.current) {
        await agreementWidgetRef.current.destroy();
        agreementWidgetRef.current = null;
      }
      widgetsRef.current = null;
    } catch (error) {
      console.error("위젯 정리 중 오류:", error);
    }

    // 결제 취소 시 updateTempReserve 호출
    if (reservationSeq) {
      updateTempReserve({
        reservationSeq: reservationSeq,
        contractCancel: true,
      });
    }

    setShowPaymentScreen(false);
  };

  // 결제 요청 처리
  const handlePaymentRequest = async () => {
    if (!widgetsRef.current || !paymentOrderData) {
      alert("결제 시스템이 준비되지 않았습니다.");
      return;
    }

    try {
      const paymentResult = await widgetsRef.current.requestPayment({
        orderId: paymentOrderData.orderId,
        orderName: `데이케어 예약 - ${storeName}`,
        customerName: paymentOrderData.customerName,
        customerEmail: paymentOrderData.customerEmail,
        successUrl: `${window.location.origin}/customer/layout/payment/success`,
        failUrl: `${window.location.origin}/customer/layout/payment/fail`,
      });

      console.log("결제 요청 성공:", paymentResult);

      // 결제 성공 시 updateTempReserve 호출
      if (reservationSeq) {
        updateTempReserve({
          reservationSeq: reservationSeq,
          contractCancel: false,
        });
        console.log("결제 성공 - 임시 예약 상태 업데이트 완료");
      }
    } catch (error: any) {
      console.error("결제 요청 실패:", error);

      if (error.code) {
        switch (error.code) {
          case "PAY_PROCESS_CANCELED":
            alert("결제가 취소되었습니다.");
            break;
          case "PAY_PROCESS_ABORTED":
            alert("결제가 중단되었습니다.");
            break;
          default:
            alert(error.message || "결제에 실패했습니다.");
        }
        if (reservationSeq) {
          updateTempReserve({
            reservationSeq: reservationSeq,
            contractCancel: true,
          });
          console.log("결제 성공 - 임시 예약 상태 업데이트 완료");
        }
      } else {
        alert("결제에 실패했습니다.");
      }
    }
  };

  // 결제 팝업 렌더링 함수
  const renderPaymentPopup = () => {
    if (!showPaymentScreen) return null;

    return (
      <div
        className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
        onClick={(e) => {
          // 모달 외부 클릭 시 닫히지 않도록 이벤트 전파 방지
          e.stopPropagation();
        }}
        style={{
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div
          className="relative w-full max-w-md mx-4 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-xl"
          onClick={(e) => {
            // 모달 내부 클릭 시 이벤트 전파 방지
            e.stopPropagation();
          }}
          style={{
            transform: "translateZ(0)", // GPU 가속 활성화
            WebkitTransform: "translateZ(0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {/* 팝업 헤더 */}
          <div className="flex sticky top-0 z-10 justify-between items-center p-4 bg-white rounded-t-lg border-b border-gray-200">
            <h2 className="text-lg font-semibold">결제</h2>
            <button
              onClick={handleClosePaymentPopup}
              className="relative p-2 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50"
              style={{ touchAction: "manipulation" }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* 팝업 내용 */}
          <div className="p-4">
            {/* 결제 정보 */}
            <div className="p-4 mb-4 bg-gray-50 rounded-lg">
              <h3 className="mb-3 text-lg font-semibold text-center">
                결제 정보
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">주문번호</span>
                  <span className="text-sm font-medium">
                    {paymentOrderData?.orderId}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">결제금액</span>
                  <span className="font-medium text-orange-400">
                    {paymentOrderData?.payAmount?.toLocaleString()}원
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">상품명</span>
                  <span className="text-sm font-medium">
                    데이케어 예약 - {storeName}
                  </span>
                </div>
              </div>
            </div>

            {/* 토스페이먼츠 결제 위젯 */}
            <div className="p-4 mb-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">결제 수단 선택</h3>
                <button
                  onClick={() => {
                    // 위젯 다시 렌더링 (통합 방식)
                    if (paymentOrderData) {
                      console.log(
                        "🔄 새로고침 버튼 클릭 - 위젯 다시 렌더링 시작"
                      );

                      const reRenderWidgets = async () => {
                        try {
                          // 기존 위젯 제거
                          if (paymentMethodWidgetRef.current) {
                            await paymentMethodWidgetRef.current.destroy();
                            paymentMethodWidgetRef.current = null;
                          }
                          if (agreementWidgetRef.current) {
                            await agreementWidgetRef.current.destroy();
                            agreementWidgetRef.current = null;
                          }

                          // SDK 재초기화
                          const clientKey = paymentOrderData.widgetClientKey;
                          const redirectUrl =
                            import.meta.env.VITE_TOSS_REDIRECT_URL ||
                            "https://app.mungtoday.com/api/payment/toss/brandpay/auth";

                          const toss = await loadTossPayments(
                            clientKey,
                            redirectUrl
                          );
                          tossPaymentsRef.current = toss;

                          // DOM 요소 대기
                          await new Promise((resolve) =>
                            setTimeout(resolve, 200)
                          );

                          // 위젯 인스턴스 생성
                          widgetsRef.current = tossPaymentsRef.current.widgets({
                            customerKey: paymentOrderData.customerKey,
                            brandpay: {
                              redirectUrl: redirectUrl,
                            },
                          });

                          // 결제 금액 설정
                          await widgetsRef.current.setAmount({
                            value: paymentOrderData.payAmount,
                            currency: "KRW",
                          });

                          // 결제 수단 UI 렌더링
                          const paymentMethodWidget =
                            await widgetsRef.current.renderPaymentMethods({
                              selector: "#payment-methods",
                              variantKey: "DEFAULT",
                            });

                          // 약관 UI 렌더링
                          const agreementWidget =
                            await widgetsRef.current.renderAgreement({
                              selector: "#agreement",
                            });

                          // 위젯 인스턴스 저장
                          paymentMethodWidgetRef.current = paymentMethodWidget;
                          agreementWidgetRef.current = agreementWidget;

                          console.log("✅ 위젯 다시 렌더링 완료");
                        } catch (error) {
                          console.error("❌ 위젯 다시 렌더링 실패:", error);
                        }
                      };
                      reRenderWidgets();
                    }
                  }}
                  className="relative px-3 py-1 text-xs text-gray-600 bg-gray-100 rounded transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
                  style={{ touchAction: "manipulation" }}
                >
                  새로고침
                </button>
              </div>
              <div id="payment-methods" className="mb-4"></div>
            </div>

            <div className="p-4 mb-4 bg-white rounded-lg border border-gray-200">
              <h3 className="mb-3 text-lg font-semibold">이용약관</h3>
              <div id="agreement" className="mb-4"></div>
            </div>

            {/* 결제 버튼 */}
            <div className="space-y-3">
              <button
                className="relative px-4 py-3 w-full text-base font-medium text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 disabled:bg-gray-300 disabled:cursor-not-allowed sm:px-6 sm:py-4 sm:text-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50"
                onClick={handlePaymentRequest}
                style={{ touchAction: "manipulation" }}
              >
                결제하기
              </button>
              <button
                className="relative px-4 py-2 w-full text-sm font-medium text-gray-600 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 sm:px-6 sm:py-3 sm:text-base focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
                onClick={handleClosePaymentPopup}
                style={{ touchAction: "manipulation" }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <form className="my-5">
        <div className="my-5">
          <Controller
            name="animal"
            control={control}
            render={({ field }) => (
              <ValidateSelectBoxImg
                label="반려동물 선택"
                required={true}
                max={1}
                value={field.value ? [field.value] : []}
                options={
                  petList
                    ? petList.map((item: PetDataType) => ({
                        label: item.petName,
                        value: item.petSeq.toString(),
                        imgSrc: item.fileUrl,
                      }))
                    : []
                }
                onChange={(val: string | string[]) => {
                  const selectedValue = Array.isArray(val) ? val[0] : val;
                  field.onChange(selectedValue);
                  setSelectedPetSeq(selectedValue);
                }}
              />
            )}
          />
        </div>
        <div className="">
          <div className="">
            <label className="block mb-2 text-sm font-semibold">
              일정 선택 <span className="text-orange-400">*</span>
            </label>
            <div className="flex gap-3 items-center mb-3">
              <span className="text-[13px] text-gray-400 min-w-[37px]">
                날짜
              </span>
              <div className="ml-2">
                <DateButton
                  value={formatDate(startDate)}
                  onClick={() => handleDateClick(true)}
                />
              </div>
            </div>
            <div className="flex gap-3 items-center mb-3">
              <span className="text-[13px] text-gray-400 min-w-[37px]">
                입소시간
              </span>
              <Controller
                name="startHour"
                control={control}
                rules={{ required: "시간을 선택하세요" }}
                render={({ field }) => (
                  <ValidateSelectList
                    {...field}
                    isDefaultValue={true}
                    placeholder="00시"
                    isSearchable={false}
                    options={startHourOptions}
                    boxHeight={20}
                    className="h-10 text-lg text-center"
                    value={field.value}
                  />
                )}
              />
              <span className="mx-1 text-[15px]">시</span>
            </div>
            <div className="flex gap-3 items-center mb-3">
              <span className="text-[13px] text-gray-400 min-w-[37px]">
                이용시간
              </span>
              <Controller
                name="useHours"
                control={control}
                rules={{ required: "이용시간을 선택하세요" }}
                render={({ field }) => (
                  <ValidateSelectList
                    {...field}
                    isDefaultValue={true}
                    placeholder="시간 선택"
                    isSearchable={false}
                    options={useHoursOptions}
                    boxHeight={20}
                    className="h-10 text-lg text-center"
                    value={field.value}
                  />
                )}
              />
            </div>
            {!hasDaycareTicket && (
              <div className="mb-3 text-[12px] text-red-500">
                보유한 데이케어 이용권이 없어 예약할 수 없습니다.
              </div>
            )}
            {watchedValues.startHour?.value &&
              watchedValues.useHours?.value && (
                <div className="flex gap-3 items-center mb-3">
                  <span className="text-[13px] text-gray-400 min-w-[37px]">
                    퇴소시간
                  </span>
                  <span className="text-[15px] font-medium">
                    {computeEndTime(
                      watchedValues.startHour.value,
                      watchedValues.useHours.value
                    )?.slice(0, 5)}
                  </span>
                </div>
              )}
            <div className="flex justify-between my-2 text-[#999] text-[11px] leading-[16.9px] mt-1">
              * 데이케어 서비스 일정은 시설 체크인/아웃을 기준으로 합니다.
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Controller
            name="services"
            control={control}
            render={({ field }) => (
              <ValidateServiceCard
                {...field}
                label="부가서비스 선택"
                options={
                  reserveForms
                    ? reserveForms.reserveForm.additionals
                        .filter(
                          (item: {
                            additionalCompanySeq?: number;
                            additionalName: string;
                            paymentCoin: number;
                            active?: boolean;
                          }) => item.active === true
                        )
                        .map(
                          (item: {
                            additionalCompanySeq?: number;
                            additionalName: string;
                            paymentCoin: number;
                          }) => ({
                            id: item.additionalCompanySeq?.toString() || "",
                            title: item.additionalName,
                            desc: item.additionalName,
                            price: item.paymentCoin.toString(),
                          })
                        )
                    : []
                }
              />
            )}
          />
        </div>
      </form>
      <Divider className="mt-auto" line />
      <div className="flex justify-between my-2">
        <div className="pt-1 mr-1 font-bold text-center text-gray-400 text-m">
          합계
        </div>
        <div className="mr-1 text-xl font-bold text-gray-400">
          {calculateCoinData?.data?.paymentCoinTotal
            ? calculateCoinData?.data?.paymentCoinTotal
            : 0}{" "}
          원
        </div>
      </div>
      <Divider className="mt-auto" line />
      <div className="flex justify-between pt-5 pb-3 text-[#999] text-[13px] leading-[16.9px] ">
        예약정보는 예약일 1일 전까지만 수정 및 취소 할 수 있습니다. 예약 당일
        서비스 미 이용 시에도 이용권은 자동으로 소진됩니다.예약 확정은
        위탁사업자 확인 후 이루어집니다.
      </div>
      <div className="flex justify-end mt-2 mr-2">
        <Controller
          name="agree"
          control={control}
          rules={{ required: "확인 및 동의가 필요합니다." }}
          render={({ field }) => (
            <ValidateCheckbox
              defaultChecked={field.value}
              onChange={() => field.onChange(!field.value)}
              // errorMessage={errors?.agree?.message}
              title="확인 및 동의"
            />
          )}
        />
      </div>
      <div className="w-full px-5 pb-6 bg-[#F5F5F5] pt-10 grid grid-cols-1 gap-5">
        <Button
          className="w-full bg-[#F5F5F5]"
          style={{ pointerEvents: "auto" }}
          onClick={handleSubmit((data) => setPendingReserveData(data))}
          disabled={!isValid || isCreatingReserve || !hasDaycareTicket}
        >
          <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
            {isCreatingReserve ? "예약 중..." : "예약 하기"}
          </div>
        </Button>
      </div>

      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          dayOnClick={(day) => {
            handleDaySelect(day);
          }}
          disablePast={true}
          selectedDay={isStart ? startDate : endDate}
          schedules={{}}
          disabledDates={disabledDates}
          handleSelectDate={handleMonthNavigate}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={closeModal}
          >
            확인
          </button>
        </div>
      </MessageModal>
      {/* 달력 모달 */}

      {/* 계약 관련 다이얼로그 */}
      <Dialog
        visible={showContractDialog}
        onClose={() => {
          setShowContractDialog(false);
          // onClose 이벤트 시 updateTempReserve 호출
          if (reservationSeq) {
            updateTempReserve({
              reservationSeq,
              contractCancel: true,
            });
          }
        }}
      >
        <div className="p-4">
          <p className="mb-4 text-center">{contractMessage}</p>
          <div className="flex gap-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
              onClick={() => {
                setShowContractDialog(false);
                // 취소 버튼 클릭 시 updateTempReserve 호출
                if (reservationSeq) {
                  updateTempReserve({
                    reservationSeq,
                    contractCancel: true,
                  });
                }
              }}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => {
                setShowContractDialog(false);
                setShowContractModal(true);
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 계약 모달 */}
      <ContractModal
        visible={showContractModal}
        onClose={() => setShowContractModal(false)}
        onConfirm={() => {
          updateTempReserve({
            reservationSeq: reservationSeq || 0,
            contractCancel: false,
          });
          setShowContractModal(false);
          setShowReservationCompleteDialog(true);
        }}
        selectedPet={petList?.find(
          (pet) => pet.petSeq.toString() === selectedPetSeq
        )}
        reservationSeq={reservationSeq || undefined}
        companySeq={parseInt(storeData.value)}
        // userSeq={1} // TODO: 실제 사용자 정보에서 가져오기
      />

      {/* 체급 확인 다이얼로그 */}
      <Dialog
        visible={showGradeConfirmDialog}
        onClose={() => setShowGradeConfirmDialog(false)}
      >
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">체급 수용 불가</h3>
          <p className="mb-6 text-sm text-gray-600">
            해당 반려견의 체급은 수용이 불가능합니다.
          </p>
          <div className="flex justify-center">
            <button
              className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => setShowGradeConfirmDialog(false)}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 코인 부족 다이얼로그 */}
      <Dialog
        visible={showCoinShortageDialog}
        onClose={() => setShowCoinShortageDialog(false)}
      >
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">멍코인이 모자라요</h3>
          <p className="mb-6 text-sm text-gray-600">
            멍코인 구매 페이지로 이동합니다.
          </p>
          <div className="flex gap-3">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
              onClick={() => setShowCoinShortageDialog(false)}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => {
                setShowCoinShortageDialog(false);
                history.push("/customer/layout/user-buy-coin");
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 중복 예약 안내 다이얼로그 */}
      <Dialog
        visible={!!duplicateReserveInfo}
        onClose={() => setDuplicateReserveInfo(null)}
      >
        <div className="p-6 text-center">
          <h3 className="mb-3 text-lg font-bold text-gray-800">
            {duplicateReserveInfo?.mode === "same"
              ? "이미 예약된 날짜입니다"
              : "다른 매장에 예약된 날짜입니다"}
          </h3>
          <p className="mb-6 text-sm text-gray-600 whitespace-pre-line">
            {duplicateReserveInfo?.mode === "same"
              ? "선택한 날짜에 같은 유형의 예약이 이미 등록되어 있어\n예약을 진행할 수 없습니다."
              : `현재 ${duplicateReserveInfo?.conflictCompanyName ?? ""}에 예약된 날짜입니다.\n예약을 진행하시겠습니까?`}
          </p>
          {duplicateReserveInfo?.mode === "same" ? (
            <div className="flex justify-center">
              <button
                className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
                onClick={() => setDuplicateReserveInfo(null)}
              >
                확인
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                className="flex-1 py-2 font-semibold text-gray-700 bg-white rounded-full border border-gray-300"
                onClick={() => setDuplicateReserveInfo(null)}
              >
                취소
              </button>
              <button
                className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                onClick={handleForceProceedReserve}
              >
                예약 진행
              </button>
            </div>
          )}
        </div>
      </Dialog>

      {/* 예약 완료 다이얼로그 */}
      <Dialog
        visible={showReservationCompleteDialog}
        onClose={() => setShowReservationCompleteDialog(false)}
      >
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">예약 신청 완료</h3>
          <div className="p-3 mb-4 bg-gray-100 rounded-lg">
            <span className="font-bold text-black">
              {startDate
                ? startDate
                    .toLocaleDateString("ko-KR", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })
                    .replace(/\. /g, ".")
                : new Date()
                    .toLocaleDateString("ko-KR", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })
                    .replace(/\. /g, ".")}
            </span>
          </div>
          <p className="mb-6 text-sm text-gray-600">
            데이케어예약이 접수되었습니다.
          </p>
          <div className="flex justify-center">
            <button
              className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => {
                setShowReservationCompleteDialog(false);
                history.push("/customer/layout/home");
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 유치원 정원 초과 안내 (단일 날짜 — 확인만) */}
      <Dialog
        visible={!!capacityFullDate}
        onClose={() => setCapacityFullDate(null)}
      >
        <div className="p-4">
          <p className="mb-4 text-center whitespace-pre-line">
            {capacityFullDate
              ? `${formatDateDot(new Date(capacityFullDate))}은\n모든 예약이 완료되었습니다.`
              : ""}
          </p>
          <button
            className="w-full py-2 font-semibold text-white rounded-full bg-primary-500"
            onClick={() => setCapacityFullDate(null)}
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 예약 신청 확인 다이얼로그 */}
      <ReserveConfirmDialog
        visible={!!pendingReserveData}
        isPending={isCreatingReserve}
        items={[
          { label: "매장", value: storeName },
          { label: "날짜", value: formatDateDot(startDate) },
          {
            label: "시간",
            value: (() => {
              const start = pendingReserveData?.startHour?.value;
              const end = computeEndTime(
                start,
                pendingReserveData?.useHours?.value
              );
              return start && end
                ? `${start}:00 ~ ${end.slice(0, 5)}`
                : null;
            })(),
          },
          {
            label: "이용시간",
            value: pendingReserveData?.useHours?.value
              ? `${pendingReserveData.useHours.value}시간`
              : null,
          },
          {
            label: "반려동물",
            value: petList?.find(
              (pet: PetDataType) => pet.petSeq.toString() === selectedPetSeq
            )?.petName,
          },
        ]}
        onCancel={() => setPendingReserveData(null)}
        onConfirm={() => {
          const data = pendingReserveData;
          setPendingReserveData(null);
          if (data) bindSubmit(data);
        }}
      />

      {/* 결제 팝업 */}
      {renderPaymentPopup()}
    </>
  );
};
export default ReservationDaycare;
